import { memo, useState } from 'react';
import { Badge, Button, TextArea } from '@aimsos/ui';
import { useTranslation } from '@aimsos/i18n';
import ConflictCompareBlock from './ConflictCompareBlock.jsx';

/** Fixtures carry a 0–1 ratio; the API may send a 0–100 score. */
function toRatio(score) {
  const numericScore = Number(score);
  if (!Number.isFinite(numericScore)) return 0;
  return Math.min(1, Math.max(0, numericScore > 1 ? numericScore / 100 : numericScore));
}

/** Prototype thresholds for a claim's extraction confidence. */
function confidenceTone(ratio, conflict) {
  if (conflict) return 'text-amber-500';
  if (ratio > 0.85) return 'text-emerald-500';
  if (ratio > 0.7) return 'text-amber-500';
  return 'text-rose-500';
}

/** Conflict cards keep the amber border; decisions highlight buttons only (EVT-001). */
function claimCardTone(claim) {
  if (claim.conflict) return 'border-amber-500/50 bg-amber-500/[0.04]';
  return 'border-edge';
}

function actionButtonVariant(kind, decision, correcting) {
  const active =
    kind === 'correct' ? decision === 'correct' || correcting : decision === kind;
  if (!active) return 'secondary';
  if (kind === 'approve') return 'positive';
  if (kind === 'reject') return 'danger';
  return 'grey';
}

/**
 * One extracted claim: id, confidence %, optional conflict compare, and
 * Approve / Reject / Correct. Per-claim decisions are local scratch state
 * until attestation — only the selected button fills in (prototype EVT-001).
 */
function ClaimRow({ claim, decision, onDecide }) {
  const { t } = useTranslation('work-queues-app');
  const [correcting, setCorrecting] = useState(false);
  const [correctionDraft, setCorrectionDraft] = useState(claim.text);
  const [correctedText, setCorrectedText] = useState(null);
  const confidence = toRatio(claim.confidence);
  const displayedText = correctedText ?? claim.text;
  const isCorrected = correctedText !== null;

  const handleDecision = (nextDecision) => {
    setCorrecting(false);
    setCorrectionDraft(claim.text);
    setCorrectedText(null);
    onDecide(claim.id, nextDecision);
  };

  const handleStartCorrection = () => {
    setCorrectionDraft(displayedText);
    setCorrecting(true);
    onDecide(claim.id, 'correct', correctedText);
  };

  const handleCancelCorrection = () => {
    setCorrectionDraft(displayedText);
    setCorrecting(false);
    onDecide(claim.id, isCorrected ? 'correct' : undefined, correctedText);
  };

  const handleSaveCorrection = () => {
    setCorrectedText(correctionDraft);
    setCorrecting(false);
    onDecide(claim.id, 'correct', correctionDraft);
  };

  const actionButtons = (
    <div className="mt-3 flex flex-wrap gap-2">
      <Button
        variant={actionButtonVariant('approve', decision, correcting)}
        size="sm"
        onClick={() => handleDecision('approve')}
      >
        {t('claims.approve')}
      </Button>
      <Button
        variant={actionButtonVariant('reject', decision, correcting)}
        size="sm"
        onClick={() => handleDecision('reject')}
      >
        {t('claims.reject')}
      </Button>
      <Button
        variant={actionButtonVariant('correct', decision, correcting)}
        size="sm"
        onClick={handleStartCorrection}
      >
        {t('claims.correct')}
      </Button>
    </div>
  );

  return (
    <div className={['rounded-xl border px-4 py-3', claimCardTone(claim)].join(' ')}>
      <div className="flex items-center gap-2">
        <span className="font-mono text-xs text-content-faint">{claim.displayId ?? claim.id}</span>
        {claim.conflict ? (
          <span className={['text-xs font-semibold uppercase tracking-wide', confidenceTone(confidence, true)].join(' ')}>
            {Math.round(confidence * 100)}% {t('claims.conflict')}
          </span>
        ) : (
          <span className={['text-xs font-medium', confidenceTone(confidence, false)].join(' ')}>
            {Math.round(confidence * 100)}%
          </span>
        )}
        {isCorrected && (
          <Badge variant="warning" size="small" className="ml-auto uppercase tracking-wide">
            {t('claims.corrected')}
          </Badge>
        )}
      </div>

      {!correcting && (
        <>
          <p className="mt-2 text-sm text-content-secondary">{displayedText}</p>
          {isCorrected && (
            <p className="mt-1 text-xs text-content-faint">
              {t('claims.originalLabel')}{' '}
              <span className="line-through">{claim.text}</span>
            </p>
          )}
        </>
      )}

      {correcting && (
        <p className="mt-2 text-sm text-content-secondary">{claim.text}</p>
      )}

      {claim.conflict && !correcting && (
        <ConflictCompareBlock sourceA={claim.sourceA} sourceB={claim.sourceB} />
      )}

      {actionButtons}

      {correcting && (
        <div className="mt-3">
          <TextArea
            rows={2}
            value={correctionDraft}
            placeholder={t('claims.correctPlaceholder')}
            onChange={(event) => setCorrectionDraft(event.target.value)}
          />
          <p className="mt-1 text-xs text-content-faint">
            {t('claims.originalClaim', { claim: claim.text })}
          </p>
          <div className="mt-2 flex justify-end gap-2">
            <Button variant="secondary" size="sm" onClick={handleCancelCorrection}>
              {t('actions.cancel')}
            </Button>
            <Button size="sm" onClick={handleSaveCorrection}>
              {t('claims.saveCorrection')}
            </Button>
          </div>
        </div>
      )}

      {correcting && claim.conflict && (
        <ConflictCompareBlock sourceA={claim.sourceA} sourceB={claim.sourceB} />
      )}
    </div>
  );
}

export default memo(ClaimRow);
